// `/app/b/:siteId/:layer` (WP1 Task 11) — ONE parameterized landing screen
// for each of the four nav layers (foundation / visibility / reputation /
// pulse). Renders one Card per TOP-LEVEL child of that layer's `buildNav()`
// group, in nav order (spec §7: "Layer landing screens: sub-feature status
// summary, then drill down") — nested children (e.g. AI visibility's
// per-engine list, Website's Domain tab) stay inside their own child screen
// and don't get their own card here. Status lines are derived honestly from
// `useOverviewSignals` — the same live-signal feed the overview screen
// already loads (T6) — so this screen fetches nothing new for that data.
// `domainActive` is the one input `useOverviewSignals` doesn't carry; it's
// read the same direct way panel-shell.tsx / mailbox-panel.tsx / domain-
// panel.tsx already do (GET /api/domains?locationId=), matching this repo's
// established per-consumer-fetch convention for that endpoint rather than
// inventing a new shared hook.
//
// Locked children (nav-model's `locked` flag — e.g. Mailboxes before a
// domain is active) render with the same lock marker AND a real link down to
// `child.to` as the sidebar's locked rows (panel-shell.tsx's NavChildRow /
// LockMarker — final-review fix, 2026-08-16, finding M2). The destination
// screen explains the lock and points the owner at the fix (e.g. Mailboxes'
// locked `to` is locationRoutes.domainWizard(businessId, siteId) — WP3b/D4's guided domain &
// mailbox wizard); there's no separate UpgradeGate wiring on this card itself.
//
// CONSOLIDATION A6 (binding, T8/T11 row): the visibility landing renders the
// same non-blocking "unverified — connect your Google Profile to verify
// ownership" notice as the map tab (google-profile.tsx) whenever the org's
// GBP connection isn't 'connected' and isn't tier-locked. The predicate
// (`needsUnverifiedBanner`) is extracted to hooks/use-gbp-data.ts so both
// screens read the identical condition instead of duplicating it.
//
// The brief's `emits panel.layer_viewed { layer }` step is VOID
// (CONSOLIDATION v2 A3 removed every client-side panel.*/gate.*/claim.*
// event) — this screen deliberately emits nothing.
import { useEffect, useState } from 'react';
import { businessApi } from '@/lib/api-paths';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useBusinessId, useTenantParams } from '@/hooks/use-tenant-params';
import { Card } from '@pageboss/ui';
import { TERMS } from '@pageboss/shared';
import { LockMarker } from '@/components/lock-marker';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { buttonVariants } from '@/components/ui/button';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { useOverviewSignals } from '@/hooks/use-overview-signals';
import { useGbpData, needsUnverifiedBanner } from '@/hooks/use-gbp-data';
import { api } from '@/lib/api';
import { buildNav, type NavChild } from '@/lib/nav-model';
import type { PathEngineInput } from '@/lib/path-engine';
import { locationRoutes, type Layer } from '@/lib/route-map';

const LAYER_TITLES: Record<Layer, string> = {
  foundation: 'Foundation',
  visibility: 'Visibility',
  reputation: 'Reputation',
  pulse: 'Pulse',
};

const VALID_LAYERS: readonly Layer[] = ['foundation', 'visibility', 'reputation', 'pulse'];

function isLayer(value: string | undefined): value is Layer {
  return !!value && (VALID_LAYERS as readonly string[]).includes(value);
}

interface DomainsListResponse {
  domains: { status: string }[];
}

export function LayerLanding() {
  const { businessId = '', locationId: siteId } = useTenantParams();
  const { layer: layerParam } = useParams<{ layer?: string }>();
  const meState = useMe();
  const signals = useOverviewSignals(businessId, siteId ?? '');
  const gbp = useGbpData(businessId, siteId ?? '');
  const [domainActive, setDomainActive] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    api
      .get<DomainsListResponse>(businessApi(businessId, `domains?locationId=${encodeURIComponent(siteId)}`))
      .then((res) => {
        if (!cancelled) setDomainActive(res.domains.some((d) => d.status === 'active'));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  if (!siteId) return null;

  // Fix round 1, MEDIUM-1: the bare `:layer` route (main.tsx) matches ANY
  // single segment under /app/b/:siteId, not just the four real layer keys —
  // an unknown value (typo, stale link) used to fall through to `!group` and
  // render blank. This check is independent of `meState`/data-loading (it's
  // pure URL validation), so it runs before either — a mistyped layer
  // segment redirects immediately rather than blanking out while data loads
  // and THEN blanking out again once it resolves. Redirects into this site's
  // overview rather than all the way out to /app (the top-level catch-all's
  // target) — friendlier for a site-scoped URL that just has one bad
  // segment, and matches precedent: every other "this isn't valid, but we
  // know the siteId" fallback in this app stays inside the site (e.g.
  // handleSwitch in panel-shell.tsx also lands on locationRoutes.overview(businessId, )).
  if (!isLayer(layerParam)) return <Navigate to={locationRoutes.overview(businessId, siteId)} replace />;

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Your business"
        title={LAYER_TITLES[layerParam]}
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready') return null;

  const me = meState.me;
  const layer = layerParam;

  const nav = buildNav({
    businessId,
    siteId,
    tier: me.usage.tier,
    domainActive,
    // Same honest default as panel-shell.tsx: no AI-tracking data source is
    // wired into either screen yet — landing cards only need the top-level
    // "AI visibility" child, never its nested per-engine list.
    trackedEngines: [],
    unreadInbox: signals.kind === 'ready' ? (signals.input.unreadInbox ?? 0) : 0,
    isOperator: me.user.isOperator,
  });

  const group = nav.groups.find((g) => g.key === layer);
  if (!group) return null;

  const input = signals.kind === 'ready' ? signals.input : null;
  const showUnverifiedNotice = layer === 'visibility' && needsUnverifiedBanner(gbp);

  return (
    <PageContainer>
      {/* Eyebrow "Your business", not the layer name: this screen IS the
          section, so repeating it above its own h1 would say nothing. It
          sits at the same level as the overview, and carries the same
          eyebrow (design audit B1). */}
      <PageHeader eyebrow="Your business" title={LAYER_TITLES[layer]} />

      {showUnverifiedNotice ? <UnverifiedNotice siteId={siteId} /> : null}

      {/* Fix round 1, MINOR: @pageboss/ui's Card hardcodes an h3 title (see
          packages/ui/src/card.tsx — no heading-level prop, and forking Card
          is out of scope here) — without an intervening h2, the page's
          heading order jumped h1 → h3. This heading closes that gap; it's
          the "sub-feature status summary" section the spec (§7) describes,
          made a real landmark instead of a skipped level. */}
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Sub-features
      </h2>

      <div className="grid gap-4 sm:grid-cols-2">
        {group.children.map((child) => (
          <LayerCard key={child.label} child={child} layer={layer} input={input} />
        ))}
      </div>
    </PageContainer>
  );
}

// Same copy + shape as google-profile.tsx's map-tab UnverifiedBanner (A6) —
// duplicated rather than shared as a component because the two call sites
// differ in layout context (this one sits above a card grid, that one above
// the map); the CONDITION is what's shared (needsUnverifiedBanner).
function UnverifiedNotice({ siteId }: { siteId: string }) {
  // Canonical tenant URLs carry the Business id; this is only rendered inside
  // one, so it comes from the router rather than being threaded as a prop.
  const businessId = useBusinessId() ?? '';
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-border-subtle bg-surface-sunken px-3 py-2.5"
    >
      <p className="text-sm text-text-secondary">
        Unverified — connect your {TERMS.googleProfile} to verify ownership of this business.
      </p>
      <Link
        to={locationRoutes.googleProfile(businessId, siteId)}
        className={buttonVariants({ variant: 'outline', size: 'sm' })}
      >
        Connect Google
      </Link>
    </div>
  );
}

function LayerCard({
  child,
  layer,
  input,
}: {
  child: NavChild;
  layer: Layer;
  input: PathEngineInput | null;
}) {
  return (
    <Card
      title={child.label}
      footer={
        child.locked ? (
          <Link
            to={child.to}
            aria-label={`${child.label} is locked — go to ${child.label}`}
            className="flex items-center justify-between gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <span>Locked</span>
            {/* The visible word above is the label — the marker is decorative
                here, or a reader hears "Locked" twice (design audit T10). */}
            <LockMarker withLabel={false} />
          </Link>
        ) : (
          <Link
            to={child.to}
            aria-label={`Go to ${child.label}`}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            Go →
          </Link>
        )
      }
    >
      <p className="text-sm text-text-secondary">{statusLine(layer, child, input)}</p>
    </Card>
  );
}

const GBP_STATE_LABEL: Record<PathEngineInput['gbpState'], string> = {
  connected: 'Connected.',
  not_connected: 'Not connected yet.',
  pending_selection: 'Finish choosing your listing.',
  locked: 'Locked on your current plan.',
  unknown: 'Not measured yet.',
};

// Honesty rule (spec §7 / path-engine.ts precedent): a null/unmeasured
// signal never renders as a fabricated zero or alarm state — every branch
// that can see `null` (or a not-yet-loaded `input`) falls to a neutral,
// non-alarming line instead. Keyed on `${layer}:${child.label}` — a direct
// coupling to nav-model.ts's labels, acceptable here because both files ship
// together and an unmatched label still renders a safe, honest default
// rather than throwing.
function statusLine(layer: Layer, child: NavChild, input: PathEngineInput | null): string {
  if (child.locked) {
    return child.lockReason === 'domain'
      ? 'Locked until you connect a domain.'
      : 'Locked on your current plan.';
  }

  // `input` is null both while useOverviewSignals is still loading AND if it
  // ever settles into its `error` state — the same neutral "not measured"
  // copy covers both rather than a "Loading…" that would be a lie once the
  // fetch has actually failed (this screen never blocks the whole card grid
  // on a signals error the way overview.tsx blocks its layer rows; the nav
  // links down still work even when the status line can't be measured).
  if (!input) return 'Not measured yet.';

  switch (`${layer}:${child.label}`) {
    case 'foundation:Business info':
      return 'Your name, address, phone, and hours.';
    case 'foundation:Website':
      return input.siteStatus === 'published' ? 'Live.' : 'Draft — not published yet.';
    case 'foundation:Mailboxes':
      // No mailbox-count signal exists on PathEngineInput (final-review fix,
      // 2026-08-16, finding L2 — this case was simply missing before, so
      // unlocked Mailboxes fell to the generic default below). Locked is
      // already handled by the `child.locked` check above this switch; this
      // branch only ever runs when a domain is active, so a static honest
      // description (same idiom as the Business info / Get-found-ready
      // cards above, which also have no per-instance signal) is correct —
      // never invent a count that isn't measured anywhere yet.
      return 'Professional email addresses on your domain.';
    case 'foundation:Get-found-ready settings':
      return 'IndexNow, llms.txt, and AI-crawler access.';
    case 'visibility:Google Profile':
      return GBP_STATE_LABEL[input.gbpState];
    case 'visibility:AI visibility':
      return input.aiTrackingEnabled ? 'Tracking enabled.' : 'Not tracking yet.';
    case 'visibility:Google Search':
      // No Search Console signal exists on PathEngineInput, and inventing
      // one here would mean a second network call per layer landing. Static
      // honest description, same idiom as the Business info / Leads cards.
      return 'Which of your pages Google has listed.';
    case 'visibility:AEO audit & tasks':
      if (input.openTasks == null) return 'Not measured yet.';
      return input.openTasks > 0
        ? `${input.openTasks} open task${input.openTasks === 1 ? '' : 's'}.`
        : 'No open tasks.';
    case 'reputation:Reviews & replies':
      // Fix round 1, MEDIUM-2: 'locked' is a tier gate, not a "you haven't
      // connected yet" state — telling a Starter-tier org to "Connect
      // Google" is a dead-end CTA (same distinction GBP_STATE_LABEL already
      // draws for the Visibility card, and the one needsUnverifiedBanner
      // draws for the notice above). Reuse that exact copy here too instead
      // of a reviews-specific rewrite.
      if (input.gbpState === 'locked') return GBP_STATE_LABEL.locked;
      if (input.gbpState !== 'connected') return 'Connect Google to see your reviews.';
      if (input.unrepliedReviews == null) return 'Not measured yet.';
      return input.unrepliedReviews > 0
        ? `${input.unrepliedReviews} review${input.unrepliedReviews === 1 ? '' : 's'} need a reply.`
        : 'All caught up.';
    case 'reputation:Reputation settings':
      return 'Review link, QR code, and reply rules.';
    case 'pulse:Inbox':
      if (input.unreadInbox == null) return 'Not measured yet.';
      return input.unreadInbox > 0
        ? `${input.unreadInbox} unread message${input.unreadInbox === 1 ? '' : 's'}.`
        : 'No unread messages.';
    case 'pulse:Leads':
      return 'A structured view of your potential customers.';
    case 'pulse:Analytics':
      return 'Traffic and engagement trends.';
    case 'pulse:Surveys':
      return 'Custom surveys for your customers.';
    default:
      return 'Manage this from here.';
  }
}
